<template>
  <Card
    v-if="showNudge"
    :class="$style.cardWrapper"
  >
    <div :class="$style.closeWrapper">
      <IconButton
        data-test-id="UpgradeLoginNudge__closeButton"
        hasNoBackground
        icon="close"
        type="secondary"
        @click="handleCloseNudge"
      />
    </div>
    <div :class="$style.highlightTagIconWrapper">
      <CustomIcon
        :class="$style.highlightTagIcon"
        :scale="1"
      >
        <HighlightTagIcon />
      </CustomIcon>
    </div>
    <ParagraphBody>
      <MarkdownInline
        data-test-id="UpgradeLoginNudge__message"
        :mdTemplate="message"
      />
    </ParagraphBody>
    <div
      :class="$style.buttonGrid"
    >
      <Button
        :aria-label="buttonText"
        data-test-id="UpgradeLoginNudge__button"
        :text="buttonText"
        type="primary"
        @click="handleCTA"
      />
      <ButtonLink
        v-if="isAdminWithBilling"
        aria-label="I need more time"
        data-test-id="upgradeLoginNudge__moreTime"
        text="I need more time"
        type=""
        @click="handleMoreTime"
      />
    </div>
  </Card>
</template>

<script>
import {
  Button,
  ButtonLink,
  Card,
  CustomIcon,
  IconButton,
  MarkdownInline,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { FeatureTrialTextConsts, trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
} from 'client/util/Constants';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapGetters } from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HighlightTagIcon from 'public/img/Payments/highlight-tag.svg?inline';
import Util from 'client/util/Util';

import UpgradeLoginNudgeController from './UpgradeLoginNudgeController';

const {
  description,
  iconName,
  location,
  section,
  text,
  toUrl,
} = eventProperties;

const {
  nudgeAlert,
} = eventLocations;

const upgradeLink = ItemTypeRoutes[ItemTypeConsts.accountUpgrade];

export default {
  name: 'UpgradeLoginNudge',

  components: {
    Button,
    ButtonLink,
    Card,
    CustomIcon,
    HighlightTagIcon,
    IconButton,
    MarkdownInline,
    ParagraphBody,
  },

  EventHelper,
  UpgradeLoginNudgeController,

  props: {
    featureDayCount: {
      type: Number,
      required: true,
    },
    featureName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      showNudge: true,
    };
  },

  computed: {
    ...mapGetters('SettingsModule', [
      'isAdmin',
      'isAdminWithBilling',
    ]),

    buttonText() {
      return this.isAdminWithBilling ? 'View Upgrade' : 'I Got It';
    },

    isAddOn() {
      return trialPropsData[this.featureName].isAddOn ? 'purchase the' : 'upgrade to the';
    },

    message() {
      const preface = `You have tried ${this.getFeatureNameFromCode(this.featureName)} for ${this.featureDayCount} days.`;
      const adminWithBillingText = 'To continue using this feature,';
      const adminText = 'To continue using this feature, ask an admin with billing to';
      const product = trialPropsData[this.featureName].featurePackage;
      const productType = trialPropsData[this.featureName].isAddOn ? 'add-on' : 'Package';
      return `${preface} ${this.isAdminWithBilling ? adminWithBillingText : adminText} ${this.isAddOn} ${product} ${productType}.`;
    },
  },

  mounted() {
    UiEvents.triggerNudgeViewed({
      description: `Upgrade Nudge for ${this.featureName} viewed`,
      page: this.$page,
      displayLogic: 'Visible 30 days after starting a trial',
    });
  },

  methods: {
    dismiss() {
      this.$options.UpgradeLoginNudgeController.dismissUpgradeLoginNudge(this.featureName);
      this.showNudge = false;
    },

    getFeatureNameFromCode(feature) {
      return FeatureTrialTextConsts[feature].featureTitle;
    },

    handleCTA() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: `opens the upgrade page for ${this.featureName}.`,
        [location]: nudgeAlert,
        [section]: 'Upgrade login nudge',
        [text]: this.buttonText,
        [toUrl]: upgradeLink,
      });

      if (this.isAdminWithBilling) {
        Util.redirect(upgradeLink);
      }

      this.handleCloseNudge();
    },

    handleCloseNudge() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: `upgrade nudge for ${this.featureName} on login is closed`,
        [iconName]: 'close',
        [location]: nudgeAlert,
        [section]: 'Upgrade login nudge',
      });

      this.dismiss();
    },

    handleMoreTime() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: `I need more time upgrade login nudge clicked for ${this.featureName}`,
        [iconName]: 'button',
        [location]: nudgeAlert,
        [section]: 'Upgrade login nudge',
      });

      this.handleCloseNudge();
    },
  },
};
</script>

<style module scoped>
.buttonGrid {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.cardWrapper {
  box-shadow: var(--jc-box-shadow-card);
  margin-top: var(--jc-spacer-small);
  position: relative;
}

.closeWrapper {
  position: absolute;
  right: var(--jc-spacer);
  top: var(--jc-spacer);
}

.highlightTagIcon path {
  fill: var(--jcSecondary);
}

.highlightTagIconWrapper {
  background-color: var(--jcPrimary100);
  border: var(--jc-border-width-double) solid var(--jcPrimary300);
  border-radius: var(--jc-border-radius-large);
  color: var(--jcSecondary);
  margin-bottom: var(--jc-spacer-small);
  padding: var(--jc-spacer-x-small) var(--jc-spacer-x-small) 0;
  width: min-content;
}

.icon {
  height: 10rem;
  width: 10rem;
}
</style>
